import React, { useState, useRef, useCallback, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import ConfirmModal from '../../components/ui/ConfirmModal';
import RubricModulesEditor from '../../components/ai/RubricModulesEditor';
import useLibraryEditImpactConfirm from './useLibraryEditImpactConfirm';
import { currentVoiceId } from '../../lib/replacedVoices';

/* ─────────────────────────────────────────────────────────────
   Voice options — mirrors AIInterviewCreate.js (same IDs)
   ───────────────────────────────────────────────────────────── */
const englishVoiceOptions = [
  {
    id: '7ea5e9c2-b719-4dc3-b870-5ba5f14d31d8',
    name: 'Janvi',
    gender: 'Janvi',
    accent: 'Indian Female',
    tone: 'Steady Agent • Warm & Professional',
    preview: '/voices/female-indian.mp3',
  },
  {
    id: '638efaaa-4d0c-442e-b701-3fae16aad012',
    name: 'Sameer',
    gender: 'Sameer',
    accent: 'Indian Male',
    tone: 'Problem Solver • Friendly & Solution-Oriented',
    preview: '/voices/sameer.mp3',
  },
];
// First entry is the default. Rohan was retired — see lib/replacedVoices.js.
const hindiVoiceOptions = [
  {
    id: '0f14d8cb-f039-41fe-a813-a9b4bee7eed8',
    name: 'Nisha',
    gender: 'Nisha',
    accent: 'Hindi Female',
    tone: 'Steady & Reassuring',
    preview: '/voices_hindi/Nisha.wav',
  },
  {
    id: '098fb15d-2597-4186-8b74-25340050b6e7',
    name: 'Vishal',
    gender: 'Vishal',
    accent: 'Hindi Male',
    tone: 'Assured & Professional',
    preview: '/voices_hindi/Vishal.wav',
  },
];
const kannadaVoiceOptions = [
  { id: '6baae46d-1226-45b5-a976-c7f9b797aae2', name: 'Prakash', gender: 'Prakash', accent: 'Kannada Male', tone: 'Firm & Articulate', preview: '/voices_kannada/prakash_kannada.wav' },
  { id: '7c6219d2-e8d2-462c-89d8-7ecba7c75d65', name: 'Divya', gender: 'Divya', accent: 'Kannada Female', tone: 'Lively & Cheerful', preview: '/voices_kannada/divya_kannada.wav' },
];
const tamilVoiceOptions = [
  { id: 'd2870b91-1b4c-47ab-81a8-3718d8e9c222', name: 'Arun', gender: 'Arun', accent: 'Tamil Male', tone: 'Expressive & Storytelling', preview: '/voices_tamil/arun_tamil.wav' },
  { id: '7f98e662-142d-41ba-89a2-12452640ce6d', name: 'Lakshmi', gender: 'Lakshmi', accent: 'Tamil Female', tone: 'Casual & Upbeat', preview: '/voices_tamil/lakshmi_tamil.wav' },
];
const bengaliVoiceOptions = [
  { id: '2ba861ea-7cdc-43d1-8608-4045b5a41de5', name: 'Rubel', gender: 'Rubel', accent: 'Bengali Male', tone: 'Casual & Everyday', preview: '/voices_bengali/Rubel_bengali.wav' },
];
const teluguVoiceOptions = [
  { id: 'cf061d8b-a752-4865-81a2-57570a6e0565', name: 'Ramya', gender: 'Ramya', accent: 'Telugu Female', tone: 'Warm & Welcoming', preview: '/voices_telugu/ramya_telugu.wav' },
];
const marathiVoiceOptions = [
  { id: 'f227bc18-3704-47fe-b759-8c78a450fdfa', name: 'Suresh', gender: 'Suresh', accent: 'Marathi Male', tone: 'Warm & Conversational', preview: '/voices_marathi/suresh_marathi.wav' },
];
const gujaratiVoiceOptions = [
  { id: '91925fe5-42ee-4ebe-96c1-c84b12a85a32', name: 'Amit', gender: 'Amit', accent: 'Gujarati Male', tone: 'Clear & Friendly', preview: '/voices_gujrathi/amit_gujarathi.wav' },
];
const malayalamVoiceOptions = [
  { id: '374b80da-e622-4dfc-90f6-1eeb13d331c9', name: 'Vijay', gender: 'Vijay', accent: 'Malayalam Male', tone: 'Clear & Conversational', preview: '/voices_malayalam/vijay_malayalam.wav' },
];
// Punjabi voice ID must stay in sync with livekit-agent/src/constants/voices.js.
const punjabiVoiceOptions = [
  { id: '8bacd442-a107-4ec1-b6f1-2fcb3f6f4d56', name: 'Gurpreet', gender: 'Gurpreet', accent: 'Punjabi Male', tone: 'Clear & Conversational', preview: '/voices_punjabi/gurpreet_punjabi.wav' },
];
// Tagalog voice ID must stay in sync with livekit-agent/src/constants/voices.js.
const tagalogVoiceOptions = [
  { id: '42332792-6e9f-4b2c-a106-3ff97e34a79d', name: 'Jerome', gender: 'Jerome', accent: 'Filipino Male', tone: 'Clear & Conversational', preview: '/voices_tagalog/jerome_tagalog.wav' },
  { id: 'd1c819ba-0384-496b-b63b-eb57a96a43cc', name: 'Angel', gender: 'Angel', accent: 'Filipino Female', tone: 'Warm & Welcoming', preview: '/voices_tagalog/angel_tagalog.wav' },
];
// French voice ID must stay in sync with livekit-agent/src/constants/voices.js.
const frenchVoiceOptions = [
  { id: '996ec149-0dca-4389-ad08-e2d6f906b4bf', name: 'Mathis', gender: 'Mathis', accent: 'French Male', tone: 'Clear & Conversational', preview: '/voices_french/mathis_french.wav' },
];
const VOICE_OPTIONS_BY_LANGUAGE = {
  en: englishVoiceOptions,
  hi: hindiVoiceOptions,
  'hi-en': hindiVoiceOptions,
  'en-hi': hindiVoiceOptions,
  kn: kannadaVoiceOptions,
  ta: tamilVoiceOptions,
  bn: bengaliVoiceOptions,
  te: teluguVoiceOptions,
  mr: marathiVoiceOptions,
  gu: gujaratiVoiceOptions,
  ml: malayalamVoiceOptions,
  pa: punjabiVoiceOptions,
  tl: tagalogVoiceOptions,
  fr: frenchVoiceOptions,
};

/* ─────────────────────────────────────────────────────────────
   Inline SVG icons (zero external icon dependency)
   ───────────────────────────────────────────────────────────── */
const Icon = ({ d, size = 16, className = '' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d={d} />
  </svg>
);
const SpinnerIcon = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    className="animate-spin"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="12" cy="12" r="10" stroke="#e5e7eb" strokeWidth="3" />
    <path d="M12 2 a10 10 0 0 1 10 10" stroke="#3b82f6" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

/* ─────────────────────────────────────────────────────────────
   Category options (matches existing AIInterviewCreate)
   ───────────────────────────────────────────────────────────── */
const CATEGORY_OPTIONS = [
  'Software Engineering', 'Marketing', 'Sales', 'Data & Analytics',
  'Design', 'Finance', 'HR', 'Operations', 'Customer Success',
  'Legal', 'Healthcare', 'Manufacturing', 'General',
];

const MODE_OPTIONS = [
  { value: 'voice', label: 'Voice Interview', desc: 'Audio-only with recording' },
  { value: 'video', label: 'Video Interview', desc: 'Camera + audio with recording' },
];

const LANGUAGE_OPTIONS = [
  { id: 'en', name: 'English', desc: 'Interviewer speaks in English', icon: 'En' },
  { id: 'hi', name: 'Hindi', desc: 'Interviewer speaks in Hindi', icon: 'हि' },
  { id: 'hi-en', name: 'Hinglish', desc: 'English questions, Hindi answers OK', icon: 'हि' },
  { id: 'en-hi', name: 'Hinglish 2', desc: 'Speaks English only; Hindi answers OK', icon: 'हि' },
  { id: 'kn', name: 'Kannada', desc: 'Interviewer speaks in Kannada', icon: 'ಕ' },
  { id: 'ta', name: 'Tamil', desc: 'Interviewer speaks in Tamil', icon: 'த' },
  { id: 'bn', name: 'Bengali', desc: 'Interviewer speaks in Bengali', icon: 'ব' },
  { id: 'te', name: 'Telugu', desc: 'Interviewer speaks in Telugu', icon: 'తె' },
  { id: 'mr', name: 'Marathi', desc: 'Interviewer speaks in Marathi', icon: 'म' },
  { id: 'gu', name: 'Gujarati', desc: 'Interviewer speaks in Gujarati', icon: 'ગ' },
  { id: 'ml', name: 'Malayalam', desc: 'Interviewer speaks in Malayalam', icon: 'മ' },
  { id: 'pa', name: 'Punjabi', desc: 'Interviewer speaks in Punjabi', icon: 'ਪ' },
  { id: 'tl', name: 'Taglish', desc: 'English by default, switches to Tagalog if needed', icon: 'Tl' },
  { id: 'fr', name: 'French', desc: 'Interviewer speaks in French', icon: 'Fr' },
];

/* Helper: generate a locally-unique key for React lists */
let _localId = 0;
const mkId = () => `local_${++_localId}`;

/* ─────────────────────────────────────────────────────────────
   Main component: RubricInterviewCreate modal
   Props:
     onClose()              – close the modal
     onSaved(templateId)    – callback after successful save
     editTemplateId         – if set, load & edit existing template
   ───────────────────────────────────────────────────────────── */
export default function RubricInterviewCreate({ onClose, onSaved, editTemplateId }) {
  /* ── Details form state ─── */
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [mode, setMode] = useState('voice');
  const [language, setLanguage] = useState('en');

  /* ── Rubric modules state ─── */
  const [modules, setModules] = useState([]);

  /* ── Intro and Outro state ─── */
  const [aiIntro, setAiIntro] = useState('Welcome, I am your AI Interviewer. This interview is designed to evaluate your qualifications for this position. The session is monitored. Let me know when you are ready.');
  const [aiOutro, setAiOutro] = useState('Thank you for completing this interview. Your responses have been recorded and will be reviewed by the team. We appreciate your time and will be in contact regarding next steps.');

  /* ── Voice state ─── */
  const [voiceId, setVoiceId] = useState(englishVoiceOptions[0].id);
  const [voiceDropdownOpen, setVoiceDropdownOpen] = useState(false);
  const [playingVoiceId, setPlayingVoiceId] = useState(null);
  const audioPreviewRef = useRef(null);
  const voiceDropdownRef = useRef(null);

  /* ── Language state ─── */
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const langDropdownRef = useRef(null);

  /* ── Save state ─── */
  const [saving, setSaving] = useState(false);

  /* ── Loading state for edit mode ─── */
  const [loadingEdit, setLoadingEdit] = useState(Boolean(editTemplateId));

  /* ── Close confirmation state ─── */
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [initialData, setInitialData] = useState(null);

  // Close voice dropdown when clicking outside
  useEffect(() => {
    if (!voiceDropdownOpen) return;
    const handler = (e) => {
      if (voiceDropdownRef.current && !voiceDropdownRef.current.contains(e.target)) {
        setVoiceDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [voiceDropdownOpen]);

  // Close language dropdown when clicking outside
  useEffect(() => {
    if (!langDropdownOpen) return;
    const handler = (e) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(e.target)) {
        setLangDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [langDropdownOpen]);

  // Stop audio preview when unmounting
  useEffect(() => {
    return () => {
      if (audioPreviewRef.current) {
        audioPreviewRef.current.pause();
        audioPreviewRef.current = null;
      }
    };
  }, []);

  // Capture initial state after load
  useEffect(() => {
    if (!loadingEdit && initialData === null) {
      setInitialData(JSON.stringify({ title, category, mode, language, voiceId, modules, aiIntro, aiOutro }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingEdit]); 

  // Load existing template when editing
  React.useEffect(() => {
    if (!editTemplateId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`/api/library/ai-interviews/${editTemplateId}`);
        const t = res.data;
        if (cancelled) return;
        setTitle(t.title || '');
        setCategory(t.category || '');
        setMode(t.config?.type || 'voice');
        const lang = t.config?.language || 'en';
        setLanguage(lang);
        // Restore saved voice or fall back to language default
        const langOptions = VOICE_OPTIONS_BY_LANGUAGE[lang] || englishVoiceOptions;
        setVoiceId(currentVoiceId(t.voiceId) || langOptions[0].id);
        setAiIntro(t.aiIntro || 'Welcome, I am your AI Interviewer. This interview is designed to evaluate your qualifications for this position. The session is monitored. Let me know when you are ready.');
        setAiOutro(t.aiOutro || 'Thank you for completing this interview. Your responses have been recorded and will be reviewed by the team. We appreciate your time and will be in contact regarding next steps.');
        if (Array.isArray(t.rubrics) && t.rubrics.length > 0) {
          setModules(
            t.rubrics.map((r) => ({
              _localId: mkId(),
              name: r.name || '',
              questionsPerInterview: r.questionsPerInterview || 1,
              questionBank: (r.questionBank || []).map((q) => ({
                _localId: mkId(),
                ...q,
              })),
            }))
          );
        }
      } catch (err) {
        if (!cancelled) {
          toast.error('Failed to load interview template.');
          onClose();
        }
      } finally {
        if (!cancelled) setLoadingEdit(false);
      }
    })();
    return () => { cancelled = true; };
  }, [editTemplateId, onClose]);

  /* ────── Details validation ────── */
  const validateDetails = () => {
    if (!title.trim()) { toast.error('Please enter an interview title.'); return false; }
    if (!category.trim()) { toast.error('Please select a category.'); return false; }
    return true;
  };

  /* ────── Voice preview ────── */
  const handleVoicePreview = useCallback((id, preview) => {
    try {
      if (audioPreviewRef.current) {
        audioPreviewRef.current.pause();
        audioPreviewRef.current.currentTime = 0;
        audioPreviewRef.current = null;
      }
      if (playingVoiceId === id) {
        setPlayingVoiceId(null);
        return;
      }
      const audio = new Audio(preview);
      audioPreviewRef.current = audio;
      setPlayingVoiceId(id);
      audio.play().catch(() => {
        setPlayingVoiceId((cur) => (cur === id ? null : cur));
        audioPreviewRef.current = null;
      });
      audio.onended = () => {
        setPlayingVoiceId((cur) => (cur === id ? null : cur));
        audioPreviewRef.current = null;
      };
    } catch (_) {
      setPlayingVoiceId(null);
    }
  }, [playingVoiceId]);

  /* ────── Safe Close ────── */
  const handleSafeClose = () => {
    const currentData = JSON.stringify({ title, category, mode, language, voiceId, modules, aiIntro, aiOutro });
    if (initialData && currentData !== initialData) {
      setShowConfirmClose(true);
    } else {
      onClose();
    }
  };

  const {
    requestSave: requestLibraryEditSave,
    confirmModal: libraryEditImpactModal,
    reportSyncResult: reportLibrarySyncResult,
  } = useLibraryEditImpactConfirm(editTemplateId);

  /* ────── Save ────── */
  const handleSave = async () => {
    if (modules.length === 0) {
      toast.error('Add at least one module before saving.');
      return;
    }
    // Validate every module
    for (const m of modules) {
      if (!m.name.trim()) {
        toast.error(`One of your modules has no name. Please name all modules.`);
        return;
      }
      if (m.questionBank.length === 0) {
        toast.error(`Module "${m.name}" has no questions. Add at least one question.`);
        return;
      }
      for (const q of m.questionBank) {
        if (!q.prompt.trim()) {
          toast.error(`Module "${m.name}" has an empty question. Fill in all questions.`);
          return;
        }
      }
    }

    // Normalise for payload — strip _localId keys
    const rubrics = modules.map((m) => ({
      name: m.name.trim(),
      questionsPerInterview: m.questionsPerInterview,
      questionBank: m.questionBank.map(({ _localId: _l, ...rest }) => rest),
    }));

    const payload = {
      title: title.trim(),
      category: category.trim(),
      questions: [], // rubric-based; questions[] is intentionally empty
      rubrics,
      advancedType: 'rubric-based',
      config: { type: mode, language, selectedTags: [] },
      voiceId: voiceId || undefined,
      aiIntro: aiIntro.trim(),
      aiOutro: aiOutro.trim(),
    };

    setSaving(true);
    try {
      if (editTemplateId) {
        const res = await axios.put(`/api/library/ai-interviews/${editTemplateId}`, payload);
        if (reportLibrarySyncResult(res.data)) {
          toast.success('Rubric-Based Interview updated successfully.');
        }
      } else {
        const res = await axios.post('/api/ai-interviews/save-to-library', payload);
        toast.success('Rubric-Based Interview saved to your library!');
        if (onSaved) onSaved(res.data?.templateId);
      }
      onClose();
    } catch (err) {
      console.error('Save error:', err);
      toast.error(err.response?.data?.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  /* ────── Render ────── */
  if (loadingEdit) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="bg-white rounded-2xl p-8 flex flex-col items-center gap-3">
          <SpinnerIcon size={32} />
          <p className="text-gray-600 text-sm">Loading interview…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 transition-opacity duration-200 bg-black/40 backdrop-blur-[2px]">
      {/* Backdrop - NO onClick to prevent accidental closing */}
      <div className="fixed inset-0" aria-hidden />

      {/* Panel */}
      <div 
        className="relative flex flex-col w-full max-w-4xl h-[70vh] max-h-[70vh] bg-white rounded-2xl shadow-2xl overflow-hidden transition-all duration-300 ease-out" 
        style={{ minHeight: '580px' }} 
        role="dialog" 
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-blue-600 flex items-center justify-center">
              <Icon d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2" size={16} className="text-white" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">
                Edit Rubric-Based Interview
              </h2>
              <p className="text-xs text-gray-500">{title || 'Custom question banks with random selection per candidate'}</p>
            </div>
          </div>
          <button type="button" onClick={handleSafeClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
            <Icon d="M18 6L6 18M6 6l12 12" size={18} />
          </button>
        </div>

        {/* ── Body ── */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-5">
              {/* Language & Voice Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                {/* Language Picker */}
                <div className="border border-gray-200 rounded-xl bg-white shadow-sm p-4">
                  <label className="block text-sm font-semibold text-gray-800 mb-1">Interview language</label>
                  <p className="text-xs text-gray-500 mb-3 min-h-[2rem]">Choose the language the interviewer will speak.</p>
                  <div className="relative" ref={langDropdownRef}>
                    {(() => {
                      const selectedLang = LANGUAGE_OPTIONS.find((l) => l.id === language);
                      return (
                        <>
                          <button
                            type="button"
                            onClick={() => {
                              setLangDropdownOpen((o) => !o);
                              setVoiceDropdownOpen(false);
                            }}
                            className={`flex w-full items-center justify-between gap-2 rounded-md border bg-white px-3 py-2 text-left text-sm shadow-sm transition-all duration-200 ${
                              langDropdownOpen ? 'border-blue-500 ring-2 ring-blue-500/25' : 'border-gray-300 hover:border-blue-400/60 hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex min-w-0 items-center gap-2">
                              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-blue-500 to-blue-700 text-white shadow-sm">
                                <span className="text-[10px] font-bold leading-none">{selectedLang?.icon || 'En'}</span>
                              </div>
                              <div className="min-w-0">
                                <p className={`truncate font-semibold ${selectedLang ? 'text-gray-900' : 'text-gray-400'}`}>
                                  {selectedLang ? selectedLang.name : 'Select language...'}
                                </p>
                                <p className="truncate text-[11px] text-gray-500">
                                  {selectedLang ? selectedLang.desc : 'Select a language'}
                                </p>
                              </div>
                            </div>
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className={`h-4 w-4 flex-shrink-0 text-gray-400 transition-transform duration-200 ${langDropdownOpen ? 'rotate-180' : ''}`}><path fillRule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06z" clipRule="evenodd" /></svg>
                          </button>
                          {langDropdownOpen && (
                            <div className="absolute z-50 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-xl max-h-64 overflow-y-auto">
                              {LANGUAGE_OPTIONS.map(({ id, name, icon }) => {
                                const isSelected = language === id;
                                return (
                                  <div
                                    key={id}
                                    className={`flex cursor-pointer items-center gap-2.5 px-3 py-2.5 transition-all ${
                                      isSelected ? 'border-l-2 border-l-blue-500 bg-blue-50' : 'border-l-2 border-l-transparent hover:bg-gray-50'
                                    }`}
                                    onClick={() => {
                                      setLanguage(id);
                                      // Reset voice to the first voice of the new language
                                      const newVoiceOptions = VOICE_OPTIONS_BY_LANGUAGE[id] || englishVoiceOptions;
                                      setVoiceId(newVoiceOptions[0].id);
                                      setLangDropdownOpen(false);
                                    }}
                                  >
                                    <div className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full ${isSelected ? 'bg-gradient-to-br from-blue-500 to-blue-700 text-white shadow-sm' : 'bg-gray-100 text-gray-500'}`}>
                                      <span className="text-xs font-bold leading-none">{icon}</span>
                                    </div>
                                    <span className={`text-sm font-semibold ${isSelected ? 'text-blue-600' : 'text-gray-900'}`}>{name}</span>
                                    {isSelected && (
                                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="ml-auto h-5 w-5 flex-shrink-0 text-blue-500"><path fillRule="evenodd" d="M19.916 4.626a.75.75 0 0 1 .208 1.04l-9 13.5a.75.75 0 0 1-1.154.114l-6-6a.75.75 0 0 1 1.06-1.06l5.353 5.353 8.493-12.74a.75.75 0 0 1 1.04-.207z" clipRule="evenodd" /></svg>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>

                {/* Voice Picker */}
                <div className="border border-gray-200 rounded-xl bg-white shadow-sm p-4 h-full">
                  <label className="block text-sm font-semibold text-gray-800 mb-1">Interviewer Voice</label>
                  <p className="text-xs text-gray-500 mb-3 min-h-[2rem]">
                    {['hi', 'hi-en', 'en-hi', 'kn', 'ta', 'bn', 'te', 'mr', 'gu', 'ml', 'pa', 'tl', 'fr'].includes(language)
                      ? 'Choose a native voice for the AI interviewer.'
                      : 'Choose the voice accent for the AI interviewer.'}
                  </p>
                  <div className="relative" ref={voiceDropdownRef}>
                    {(() => {
                      const voiceOptions = VOICE_OPTIONS_BY_LANGUAGE[language] || englishVoiceOptions;
                      const selectedVoice = voiceOptions.find((v) => v.id === voiceId) || voiceOptions[0];
                      return (
                        <>
                          <button
                            type="button"
                            onClick={() => {
                              setVoiceDropdownOpen((o) => !o);
                              setLangDropdownOpen(false);
                            }}
                            className={`flex w-full items-center justify-between gap-2 rounded-md border bg-white px-3 py-2 text-left text-sm shadow-sm transition-all duration-200 ${
                              voiceDropdownOpen ? 'border-blue-500 ring-2 ring-blue-500/25' : 'border-gray-300 hover:border-blue-400/60 hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex min-w-0 items-center gap-2">
                              <div
                                className="flex h-8 w-8 flex-shrink-0 cursor-pointer items-center justify-center rounded-md bg-blue-50 text-blue-600 transition-colors hover:bg-blue-100"
                                onClick={(e) => { e.stopPropagation(); handleVoicePreview(selectedVoice.id, selectedVoice.preview); }}
                                title="Preview voice"
                              >
                                {playingVoiceId === selectedVoice.id ? (
                                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4"><path fillRule="evenodd" d="M6.75 5.25a.75.75 0 0 1 .75-.75H9a.75.75 0 0 1 .75.75v13.5a.75.75 0 0 1-.75.75H7.5a.75.75 0 0 1-.75-.75V5.25zm7.5 0A.75.75 0 0 1 15 4.5h1.5a.75.75 0 0 1 .75.75v13.5a.75.75 0 0 1-.75.75H15a.75.75 0 0 1-.75-.75V5.25z" clipRule="evenodd" /></svg>
                                ) : (
                                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4"><path fillRule="evenodd" d="M4.5 5.653c0-1.427 1.529-2.33 2.779-1.643l11.54 6.347c1.295.712 1.295 2.573 0 3.286L7.28 19.99c-1.25.687-2.779-.217-2.779-1.643V5.653z" clipRule="evenodd" /></svg>
                                )}
                              </div>
                              <div className="min-w-0">
                                <p className="truncate font-semibold text-gray-900">
                                  {selectedVoice.gender}
                                  <span className="font-medium text-gray-400"> · </span>
                                  <span className="font-medium text-gray-700">{selectedVoice.accent}</span>
                                </p>
                                <p className="truncate text-[11px] text-gray-500">{selectedVoice.tone}</p>
                              </div>
                            </div>
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className={`h-4 w-4 flex-shrink-0 text-gray-400 transition-transform duration-200 ${voiceDropdownOpen ? 'rotate-180' : ''}`}><path fillRule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06z" clipRule="evenodd" /></svg>
                          </button>

                          {voiceDropdownOpen && (
                            <div className="absolute z-50 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-xl max-h-64 overflow-y-auto">
                              {voiceOptions.map(({ id, gender, accent, tone, preview }) => {
                                const isSelected = voiceId === id;
                                return (
                                  <div
                                    key={id}
                                    className={`flex items-center gap-2.5 px-3 py-2.5 cursor-pointer transition-all ${
                                      isSelected ? 'bg-blue-50 border-l-2 border-l-blue-500' : 'hover:bg-gray-50 border-l-2 border-l-transparent'
                                    }`}
                                    onClick={() => { setVoiceId(id); setVoiceDropdownOpen(false); }}
                                  >
                                    <div
                                      className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                                        playingVoiceId === id
                                          ? 'bg-gradient-to-br from-blue-500 to-blue-700 text-white shadow-sm'
                                          : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                      }`}
                                      onClick={(e) => { e.stopPropagation(); handleVoicePreview(id, preview); }}
                                      title="Preview voice"
                                    >
                                      {playingVoiceId === id ? (
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path fillRule="evenodd" d="M6.75 5.25a.75.75 0 0 1 .75-.75H9a.75.75 0 0 1 .75.75v13.5a.75.75 0 0 1-.75.75H7.5a.75.75 0 0 1-.75-.75V5.25zm7.5 0A.75.75 0 0 1 15 4.5h1.5a.75.75 0 0 1 .75.75v13.5a.75.75 0 0 1-.75.75H15a.75.75 0 0 1-.75-.75V5.25z" clipRule="evenodd" /></svg>
                                      ) : (
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path fillRule="evenodd" d="M4.5 5.653c0-1.427 1.529-2.33 2.779-1.643l11.54 6.347c1.295.712 1.295 2.573 0 3.286L7.28 19.99c-1.25.687-2.779-.217-2.779-1.643V5.653z" clipRule="evenodd" /></svg>
                                      )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        <span className={`text-sm font-semibold ${isSelected ? 'text-blue-600' : 'text-gray-900'}`}>{gender}</span>
                                        <span className="text-gray-300">•</span>
                                        <span className={`text-sm font-medium ${isSelected ? 'text-blue-600' : 'text-gray-700'}`}>{accent}</span>
                                      </div>
                                      <p className="text-xs text-gray-500">{tone}</p>
                                    </div>
                                    {isSelected && (
                                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-blue-500 flex-shrink-0"><path fillRule="evenodd" d="M19.916 4.626a.75.75 0 0 1 .208 1.04l-9 13.5a.75.75 0 0 1-1.154.114l-6-6a.75.75 0 0 1 1.06-1.06l5.353 5.353 8.493-12.74a.75.75 0 0 1 1.04-.207z" clipRule="evenodd" /></svg>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Click the play button to preview the voice.</p>
                </div>
              </div>

              {/* Intro Settings */}
              <div className="border border-gray-200 rounded-xl bg-white shadow-sm p-4">
                <label className="block text-sm font-semibold text-gray-800 mb-1">Intro Message</label>
                <p className="text-xs text-gray-500 mb-3">The AI will say this when the interview begins.</p>
                <textarea
                  rows={3}
                  value={aiIntro}
                  onChange={(e) => setAiIntro(e.target.value)}
                  placeholder="Welcome, I am your AI Interviewer..."
                  className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent resize-none"
                />
              </div>

              <RubricModulesEditor
                modules={modules}
                setModules={setModules}
                interviewTitle={title}
                showUploadButton={true}
              />



              {/* Outro Settings */}
              <div className="border border-gray-200 rounded-xl bg-white shadow-sm p-4">
                <label className="block text-sm font-semibold text-gray-800 mb-1">Outro Message</label>
                <p className="text-xs text-gray-500 mb-3">The AI will say this when the interview concludes.</p>
                <textarea
                  rows={3}
                  value={aiOutro}
                  onChange={(e) => setAiOutro(e.target.value)}
                  placeholder="Thank you for completing this interview..."
                  className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent resize-none"
                />
              </div>
            </div>
          </div>

        {/* ── Footer ── */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-gray-200 bg-white flex items-center justify-between rounded-b-2xl">
          <button
            type="button"
            onClick={handleSafeClose}
            className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={() => requestLibraryEditSave(handleSave)}
            disabled={saving || modules.length === 0}
            className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors shadow-sm"
          >
            {saving ? (
              <>
                <SpinnerIcon size={14} />
                Saving…
              </>
            ) : (
              <>
                <Icon d="M5 13l4 4L19 7" size={14} className="text-white" />
                {editTemplateId ? 'Save Changes' : 'Save to AI Interview Library'}
              </>
            )}
          </button>
        </div>
      </div>

      {libraryEditImpactModal}
      <ConfirmModal
        isOpen={showConfirmClose}
        onClose={() => setShowConfirmClose(false)}
        onConfirm={onClose}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to close without saving?"
        confirmLabel="Yes, Discard Changes"
        cancelLabel="No, Keep Editing"
        variant="warning"
      />
    </div>
  );
}
